import { flatMap, max, omit, size } from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  SearchRepository,
  SearchRepositoryUID,
} from "@radarly/core/search/repositories/SearchRepository";
import { SearchFragmentPosts } from "@radarly/models/search";

const POSTS_PER_PAGE = 20;

export function useSearchPosts(
  projectId: number,
  request: SearchFragmentPosts["request"]
): {
  posts: SearchFragmentPosts["data"]["hits"];
  total: SearchFragmentPosts["data"]["total"];
  isLoading: boolean;
  isFetchingMore: boolean;
  canFetchMore: boolean;
  fetchMore: () => void;
} {
  const repository = useInjection<SearchRepository>(SearchRepositoryUID);
  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteQuery(
      ["/search/v3", projectId, request],
      ({ pageParam = 0 }) => {
        const _request = {
          ...omit(request, "name"),
          pagination: {
            sort: request.pagination.sort,
            limit: POSTS_PER_PAGE,
            start: pageParam,
          },
        };
        return repository.searchFragmentData(projectId, _request) as Promise<
          SearchFragmentPosts["data"]
        >;
      },
      {
        getNextPageParam: ({ hits }, groups) => {
          if (size(hits)) {
            return size(groups) * POSTS_PER_PAGE;
          } else {
            return false;
          }
        },
      }
    );
  return {
    posts: data ? flatMap(data.pages, (d) => d.hits) : [],
    total: data ? max(flatMap(data.pages, (d) => d.total)) : 0,
    isFetchingMore: !!isFetchingNextPage,
    fetchMore: fetchNextPage,
    isLoading,
    canFetchMore: hasNextPage,
  };
}
